import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useCompanyAuth } from '../../context/CompanyAuthContext';
import {
  getNotifications, markAllNotifsRead, markNotifRead,
  getChatMessages, sendChatMessage, getTeam, globalSearch,
} from '../../services/companyApi';

const F = "'Inter',-apple-system,sans-serif";
const UPLOAD_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');

const NOTIF_ICON = {
  approval_request: '📋',
  approved:         '✅',
  rejected:         '↩️',
  new_application:  '📥',
  scan_complete:    '🔍',
};

export default function CompanyLayout({ children }) {
  const { companyUser, logout } = useCompanyAuth();
  const location  = useLocation();
  const navigate  = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 768);
  const [isMobile,    setIsMobile]    = useState(window.innerWidth < 768);

  useEffect(() => {
    const fn = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      setSidebarOpen(!mobile);
    };
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  useEffect(() => { if (isMobile) setSidebarOpen(false); }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const role          = companyUser?.role;
  const isHiringMgr   = role === 'hiring_manager';
  const canSeeCredits = ['owner', 'admin'].includes(role);
  const canSeeAdmin   = ['owner', 'admin'].includes(role);

  const NAV = isHiringMgr ? [
    // Hiring manager: department-scoped view only
    { to: '/company/dashboard', icon: 'ti-layout-dashboard', label: 'Dashboard'  },
    { to: '/company/recruit',   icon: 'ti-briefcase',        label: 'My Jobs'    },
    { to: '/company/candidates',icon: 'ti-address-book',     label: 'Candidates' },
    { to: '/company/profile',   icon: 'ti-building',         label: 'Profile'    },
  ] : [
    { to: '/company/dashboard',         icon: 'ti-layout-dashboard', label: 'Dashboard'       },
    { to: '/company/assessments',        icon: 'ti-file-description', label: 'Assessments'     },
    { to: '/company/recruit',            icon: 'ti-briefcase',        label: 'Recruit'          },
    { to: '/company/interviews',         icon: 'ti-calendar-event',   label: 'Interviews'      },
    { to: '/company/post-hire',          icon: 'ti-clipboard-check',   label: 'Post-Hire'       },
    { to: '/company/sla',               icon: 'ti-clock-exclamation', label: 'SLA Deadlines'   },
    { to: '/company/cv-scan',            icon: 'ti-file-search',      label: 'CV Scanning'     },
    { to: '/company/candidates',         icon: 'ti-address-book',     label: 'Candidates'      },
    { to: '/company/skills-tests',       icon: 'ti-school',           label: 'Skills Tests'    },
    { to: '/company/re-engage',          icon: 'ti-mail-forward',     label: 'Re-engagement'   },
    { to: '/company/talent-community',   icon: 'ti-world',            label: 'Talent Community'},
    { to: '/company/hiring-requests',    icon: 'ti-git-pull-request', label: 'Hiring Requests' },
    { to: '/company/team',               icon: 'ti-users',            label: 'Team'            },
    { to: '/company/departments',        icon: 'ti-sitemap',          label: 'Departments'     },
    ...(canSeeCredits ? [{ to: '/company/credits', icon: 'ti-coin', label: 'Credits' }] : []),
    ...(canSeeAdmin ? [
      { to: '/company/white-label',      icon: 'ti-brand-open-source', label: 'White-label'    },
      { to: '/company/analytics',        icon: 'ti-chart-bar',        label: 'Analytics'       },
      { to: '/company/brand-analytics',  icon: 'ti-chart-line',       label: 'Brand Analytics' },
      { to: '/company/diversity',        icon: 'ti-gender-bigender',  label: 'D&I Reports'     },
      { to: '/company/widget',           icon: 'ti-code',             label: 'Job Board Widget'},
      { to: '/company/audit-log',        icon: 'ti-clipboard-list',   label: 'Audit Log'       },
      { to: '/company/email-templates',     icon: 'ti-mail',             label: 'Email Templates'    },
      { to: '/company/automation',          icon: 'ti-robot',            label: 'Automation'         },
      { to: '/company/pipeline-templates',  icon: 'ti-template',         label: 'Pipeline Templates' },
      { to: '/company/webhooks',            icon: 'ti-webhook',          label: 'Webhooks'           },
      { to: '/company/gdpr',             icon: 'ti-shield-lock',      label: 'GDPR / Data'     },
    ] : []),
    { to: '/company/profile',            icon: 'ti-building',         label: 'Profile'         },
    { to: '/company/help',              icon: 'ti-help-circle',      label: 'Help & Guide'    },
  ];

  // Global search
  const [showSearch, setShowSearch]   = useState(false);
  const [searchQ, setSearchQ]         = useState('');
  const [searchRes, setSearchRes]     = useState(null);
  const [searchBusy, setSearchBusy]   = useState(false);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const fn = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearch(v => !v);
        setSearchQ('');
        setSearchRes(null);
      }
      if (e.key === 'Escape') setShowSearch(false);
    };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, []);

  useEffect(() => {
    if (showSearch) setTimeout(() => searchInputRef.current?.focus(), 50);
  }, [showSearch]);

  useEffect(() => {
    if (!searchQ.trim()) { setSearchRes(null); return; }
    const t = setTimeout(async () => {
      setSearchBusy(true);
      try {
        const r = await globalSearch(searchQ.trim());
        setSearchRes(r.data);
      } catch { /* silent */ }
      finally { setSearchBusy(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [searchQ]);

  const SEARCH_TYPE_LABEL = { candidate: 'Candidates', application: 'Applications', assessment: 'Assessments', job: 'Jobs' };
  const SEARCH_TYPE_HREF  = { candidate: id => `/company/candidates`, application: id => `/company/recruit`, assessment: id => `/company/assessments/${id}`, job: id => `/company/recruit/jobs/${id}` };

  // Notifications
  const [showNotifs, setShowNotifs]   = useState(false);
  const [notifs, setNotifs]           = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const notifsRef = useRef(null);

  // Chat state
  const [showChat, setShowChat]       = useState(false);
  const [chatRecipient, setChatRecipient] = useState(null); // null = group, uuid = DM
  const [teamMembers, setTeamMembers] = useState([]);
  const [messages, setMessages]       = useState([]);
  const [chatInput, setChatInput]     = useState('');
  const [chatFiles, setChatFiles]     = useState([]);
  const [chatSending, setChatSending] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [dmUnread, setDmUnread]       = useState({}); // { recipientId: latestTimestamp }
  const chatBottomRef = useRef(null);
  const chatFileRef   = useRef(null);
  const pollRef       = useRef(null);
  const lastSeenRef   = useRef({}); // { recipientId|'everyone': latestTimestamp }

  const loadNotifs = useCallback(async () => {
    try {
      const r = await getNotifications();
      setNotifs(r.data.notifications || []);
      setUnreadCount(r.data.unread_count || 0);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    loadNotifs();
    const t = setInterval(loadNotifs, 30000);
    return () => clearInterval(t);
  }, [loadNotifs]);

  useEffect(() => {
    const handler = (e) => {
      if (notifsRef.current && !notifsRef.current.contains(e.target)) setShowNotifs(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleMarkAllRead = async () => {
    await markAllNotifsRead().catch(() => {});
    setNotifs(prev => prev.map(n => ({ ...n, is_read: true })));
    setUnreadCount(0);
  };

  const handleNotifClick = async (n) => {
    if (!n.is_read) {
      markNotifRead(n.id).catch(() => {});
      setNotifs(prev => prev.map(x => x.id === n.id ? { ...x, is_read: true } : x));
      setUnreadCount(c => Math.max(0, c - 1));
    }
    setShowNotifs(false);
    if (n.link) navigate(n.link);
  };

  // Load team members when chat opens (for DM selector)
  useEffect(() => {
    if (showChat && teamMembers.length === 0) {
      getTeam().then(r => setTeamMembers((r.data || []).filter(m => m.id !== companyUser?.id))).catch(() => {});
    }
  }, [showChat, companyUser?.id, teamMembers.length]);

  // Load messages for current conversation
  const loadChat = useCallback(async () => {
    if (chatLoading) return;
    setChatLoading(true);
    try {
      const r = await getChatMessages(chatRecipient);
      setMessages(r.data);
    } catch { /* silent */ }
    finally { setChatLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatRecipient]);

  // Poll chat when open; also background-poll other DM channels for unread dots
  useEffect(() => {
    if (!showChat) { clearInterval(pollRef.current); return; }
    loadChat();
    pollRef.current = setInterval(async () => {
      try {
        const r = await getChatMessages(chatRecipient);
        setMessages(r.data);
        // Track last-seen so we can clear the unread dot for current channel
        const key = chatRecipient || 'everyone';
        if (r.data.length) lastSeenRef.current[key] = r.data[r.data.length - 1].created_at;
        setDmUnread(prev => { const n = { ...prev }; delete n[key]; return n; });
      } catch (e) { console.warn('[chat poll]', e.message); }
    }, 5000);
    return () => clearInterval(pollRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showChat, chatRecipient]);

  // Background-poll other DM channels when chat is open to surface unread dots
  useEffect(() => {
    if (!showChat || teamMembers.length === 0) return;
    const bgPoll = setInterval(async () => {
      for (const m of teamMembers) {
        if (m.id === chatRecipient) continue; // already polling this one
        try {
          const r = await getChatMessages(m.id);
          if (!r.data.length) continue;
          const latest = r.data[r.data.length - 1].created_at;
          const seen   = lastSeenRef.current[m.id];
          if (!seen || new Date(latest) > new Date(seen)) {
            setDmUnread(prev => ({ ...prev, [m.id]: latest }));
          }
        } catch { /* ignore */ }
      }
    }, 15000); // less frequent than main poll
    return () => clearInterval(bgPoll);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showChat, teamMembers, chatRecipient]);

  useEffect(() => {
    if (showChat) setTimeout(() => chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  }, [messages, showChat]);

  const switchConversation = (recipientId) => {
    setChatRecipient(recipientId);
    setMessages([]);
    // Clear unread dot when switching to that DM
    const key = recipientId || 'everyone';
    setDmUnread(prev => { const n = { ...prev }; delete n[key]; return n; });
  };

  const handleSendChat = async () => {
    if (!chatInput.trim() && !chatFiles.length) return;
    setChatSending(true);
    try {
      const fd = new FormData();
      if (chatInput.trim()) fd.append('body', chatInput.trim());
      if (chatRecipient) fd.append('recipient_id', chatRecipient);
      chatFiles.forEach(f => fd.append('files', f));
      const r = await sendChatMessage(fd);
      setMessages(prev => [...prev, r.data]);
      setChatInput('');
      setChatFiles([]);
      if (chatFileRef.current) chatFileRef.current.value = '';
    } catch { toast.error('Failed to send message'); }
    finally { setChatSending(false); }
  };

  const [confirmLogout, setConfirmLogout] = useState(false);
  const handleLogout = () => { logout(); navigate('/company/login'); };

  const myInitial = companyUser?.full_name?.[0]?.toUpperCase() || '?';
  const recipientName = chatRecipient ? (teamMembers.find(m => m.id === chatRecipient)?.full_name || 'DM') : null;

  const fileUrl = (url) => url?.startsWith('http') ? url : `${UPLOAD_BASE}${url}`;

  const open = sidebarOpen || isMobile;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', fontFamily: F }}>
      {/* Mobile backdrop */}
      {isMobile && sidebarOpen && (
        <div onClick={() => setSidebarOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 999, backdropFilter: 'blur(2px)' }} />
      )}

      {/* Sidebar */}
      <aside style={{
        width: isMobile ? 260 : (sidebarOpen ? 240 : 64),
        background: '#0F172A',
        display: 'flex', flexDirection: 'column', flexShrink: 0,
        transition: isMobile ? 'left 0.25s ease' : 'width 0.2s ease',
        overflow: 'hidden',
        ...(isMobile ? {
          position: 'fixed', top: 0, left: sidebarOpen ? 0 : -260,
          height: '100vh', zIndex: 1000,
          boxShadow: sidebarOpen ? '4px 0 24px rgba(0,0,0,0.25)' : 'none',
        } : { position: 'sticky', top: 0, height: '100vh' }),
      }}>
        {/* Logo row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 14px', height: 56, borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
          {open && (
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em', lineHeight: 1.2 }}>
                Centric<span style={{ color: '#C1272D' }}>Q</span>
              </div>
              <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Company Portal</div>
            </div>
          )}
          <button onClick={() => setSidebarOpen(o => !o)} aria-label="Toggle sidebar"
            style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', padding: 4, display: 'flex', marginLeft: open ? 0 : 'auto', marginRight: open ? 0 : 'auto' }}>
            <i className={`ti ${sidebarOpen ? 'ti-layout-sidebar-left-collapse' : 'ti-layout-sidebar-left-expand'}`} style={{ fontSize: 20 }} />
          </button>
        </div>

        {/* Company card */}
        {open && (
          <div style={{ margin: '10px 10px 8px', background: 'rgba(255,255,255,0.07)', borderRadius: 10, padding: '10px 12px' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#fff', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {companyUser?.company_name}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden' }}>
              <span style={{ fontSize: 10, color: '#fff', fontWeight: 600, background: 'rgba(255,255,255,0.15)', padding: '2px 7px', borderRadius: 4, flexShrink: 0 }}>
                {companyUser?.role}
              </span>
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {companyUser?.full_name}
              </span>
            </div>
          </div>
        )}

        <nav style={{ flex: 1, padding: open ? '0 10px' : '4px 0', overflowY: 'auto' }}>
          {NAV.map(({ to, icon, label }) => {
            const active = location.pathname.startsWith(to);
            return (
              <Link key={to} to={to} style={{
                display: 'flex', alignItems: 'center', gap: open ? 10 : 0,
                padding: open ? '9px 12px' : '10px 0',
                justifyContent: open ? 'flex-start' : 'center',
                borderRadius: 8, marginBottom: 2, textDecoration: 'none',
                background: active ? 'rgba(255,255,255,0.12)' : 'transparent',
                color: active ? '#fff' : 'rgba(255,255,255,0.55)',
                fontSize: 13, fontWeight: active ? 600 : 400,
                borderLeft: open ? (active ? '3px solid #C1272D' : '3px solid transparent') : 'none',
              }}>
                <i className={icon} style={{ fontSize: 17, flexShrink: 0 }} />
                {open && label}
              </Link>
            );
          })}
        </nav>

        <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', padding: open ? '10px 10px 16px' : '10px 0 16px', flexShrink: 0 }}>
          {open ? (
            <>
              <button onClick={() => { setShowSearch(true); setSearchQ(''); setSearchRes(null); }} style={{
                width: '100%', padding: '8px 12px', background: 'transparent',
                border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, marginBottom: 5,
                color: 'rgba(255,255,255,0.7)', fontSize: 12, cursor: 'pointer', fontFamily: F,
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                Search
                <span style={{ marginLeft: 'auto', fontSize: 10, background: 'rgba(255,255,255,0.12)', borderRadius: 4, padding: '1px 5px', letterSpacing: '0.02em' }}>⌘K</span>
              </button>
              <button onClick={() => setShowChat(v => !v)} style={{
                width: '100%', padding: '8px 12px', background: showChat ? 'rgba(255,255,255,0.12)' : 'transparent',
                border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, marginBottom: 5,
                color: 'rgba(255,255,255,0.7)', fontSize: 12, cursor: 'pointer', fontFamily: F,
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
                Team Chat
              </button>

              <div ref={notifsRef} style={{ position: 'relative', marginBottom: 5 }}>
                <button onClick={() => setShowNotifs(v => !v)} style={{
                  width: '100%', padding: '8px 12px', background: showNotifs ? 'rgba(255,255,255,0.12)' : 'transparent',
                  border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8,
                  color: 'rgba(255,255,255,0.7)', fontSize: 12, cursor: 'pointer', fontFamily: F,
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <span style={{ position: 'relative', display: 'inline-flex' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                    </svg>
                    {unreadCount > 0 && (
                      <span style={{ position: 'absolute', top: -5, right: -6, background: '#C1272D', color: '#fff', fontSize: 9, fontWeight: 700, borderRadius: '50%', width: 14, height: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </span>
                  Notifications
                </button>

            {showNotifs && (
              <div style={{
                position: 'fixed', bottom: 80, left: 12, width: 320, background: '#fff',
                borderRadius: 12, border: '1px solid #E2E8F0', boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                zIndex: 400, overflow: 'hidden',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #F1F5F9' }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }}>Notifications</span>
                  {unreadCount > 0 && (
                    <button onClick={handleMarkAllRead} style={{ fontSize: 11, color: '#2563EB', background: 'none', border: 'none', cursor: 'pointer', fontFamily: F, fontWeight: 600 }}>
                      Mark all read
                    </button>
                  )}
                </div>
                <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                  {notifs.length === 0 ? (
                    <div style={{ padding: '24px 16px', textAlign: 'center', fontSize: 13, color: '#94A3B8' }}>No notifications yet</div>
                  ) : notifs.map(n => (
                    <div key={n.id} onClick={() => handleNotifClick(n)} style={{
                      padding: '12px 16px', cursor: 'pointer', display: 'flex', gap: 10, alignItems: 'flex-start',
                      background: n.is_read ? '#fff' : '#EFF6FF',
                      borderBottom: '1px solid #F1F5F9',
                    }}>
                      <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>{NOTIF_ICON[n.type] || '🔔'}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: n.is_read ? 400 : 700, color: '#0F172A', marginBottom: 2, wordBreak: 'break-word' }}>{n.title}</div>
                        <div style={{ fontSize: 11, color: '#64748B', lineHeight: 1.5, wordBreak: 'break-word' }}>{n.body}</div>
                        <div style={{ fontSize: 10, color: '#94A3B8', marginTop: 4 }}>
                          {new Date(n.created_at).toLocaleString()}
                        </div>
                      </div>
                      {!n.is_read && <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#2563EB', flexShrink: 0, marginTop: 4 }} />}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

              {confirmLogout ? (
                <div style={{ display: 'flex', gap: 6, marginTop: 5 }}>
                  <button onClick={handleLogout}
                    style={{ flex: 1, padding: '7px 10px', background: '#C1272D', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: F }}>
                    Yes, sign out
                  </button>
                  <button onClick={() => setConfirmLogout(false)}
                    style={{ flex: 1, padding: '7px 10px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: 'rgba(255,255,255,0.7)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: F }}>
                    Cancel
                  </button>
                </div>
              ) : (
                <button onClick={() => setConfirmLogout(true)} style={{
                  width: '100%', padding: '8px 12px', background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8,
                  color: 'rgba(255,255,255,0.7)', fontSize: 12, cursor: 'pointer', fontFamily: F,
                  display: 'flex', alignItems: 'center', gap: 8, marginTop: 5,
                }}>
                  <i className="ti-logout" style={{ fontSize: 14 }} />
                  Sign out
                </button>
              )}
            </>
          ) : (
            /* Collapsed: icon-only buttons stacked */
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '4px 0' }}>
              <button onClick={() => setShowChat(v => !v)} title="Team Chat"
                style={{ background: showChat ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.06)', border: 'none', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', padding: 8, borderRadius: 8, display: 'flex' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
              </button>
              <div ref={notifsRef} style={{ position: 'relative' }}>
                <button onClick={() => setShowNotifs(v => !v)} title="Notifications"
                  style={{ background: showNotifs ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.06)', border: 'none', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', padding: 8, borderRadius: 8, display: 'flex', position: 'relative' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                  </svg>
                  {unreadCount > 0 && (
                    <span style={{ position: 'absolute', top: 2, right: 2, background: '#C1272D', color: '#fff', fontSize: 8, fontWeight: 700, borderRadius: '50%', width: 12, height: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
              </div>
              {confirmLogout ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <button onClick={handleLogout} title="Confirm sign out"
                    style={{ background: '#C1272D', border: 'none', color: '#fff', cursor: 'pointer', padding: '4px 6px', borderRadius: 5, fontSize: 10, fontWeight: 700, fontFamily: F }}>Yes</button>
                  <button onClick={() => setConfirmLogout(false)} title="Cancel"
                    style={{ background: 'rgba(255,255,255,0.12)', border: 'none', color: 'rgba(255,255,255,0.8)', cursor: 'pointer', padding: '4px 6px', borderRadius: 5, fontSize: 10, fontWeight: 600, fontFamily: F }}>No</button>
                </div>
              ) : (
                <button onClick={() => setConfirmLogout(true)} title="Sign out" aria-label="Sign out"
                  style={{ background: 'rgba(255,255,255,0.06)', border: 'none', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', padding: 8, borderRadius: 8, display: 'flex' }}>
                  <i className="ti-logout" style={{ fontSize: 16 }} />
                </button>
              )}
            </div>
          )}
        </div>
      </aside>

      <main style={{ flex: 1, background: '#F0F6FF', minHeight: '100vh', overflow: 'auto', paddingTop: isMobile ? 48 : 0 }}>
        {isMobile && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: 48, background: '#1B3A5C', display: 'flex', alignItems: 'center', padding: '0 16px', zIndex: 998, gap: 12 }}>
            <button onClick={() => setSidebarOpen(o => !o)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 4, display: 'flex' }}>
              <i className="ti ti-menu-2" style={{ fontSize: 22 }} />
            </button>
            <span style={{ fontSize: 15, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em' }}>Centric<span style={{ color: '#C1272D' }}>Q</span></span>
          </div>
        )}
        {children}
      </main>

      {/* ── Chat slide-out panel ──────────────────────────────────────────── */}
      {showChat && (
        <div style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, width: 380,
          background: '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
          display: 'flex', flexDirection: 'column', zIndex: 300, fontFamily: F,
        }}>
          {/* Chat header */}
          <div style={{ padding: '14px 16px', borderBottom: '1px solid #E2E8F0', background: '#0F172A', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>
                {chatRecipient ? `DM: ${recipientName}` : 'Team Chat (Everyone)'}
              </div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>{companyUser?.company_name}</div>
            </div>
            <button onClick={() => setShowChat(false)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: 20, cursor: 'pointer' }}>×</button>
          </div>

          {/* Conversation selector */}
          <div style={{ padding: '8px 12px', borderBottom: '1px solid #F1F5F9', display: 'flex', gap: 6, overflowX: 'auto', flexShrink: 0 }}>
            <button onClick={() => switchConversation(null)}
              style={{ padding: '5px 12px', borderRadius: 20, border: '1px solid', fontSize: 11, fontWeight: 600, cursor: 'pointer', flexShrink: 0, position: 'relative',
                borderColor: !chatRecipient ? '#1B3A5C' : '#E2E8F0',
                background: !chatRecipient ? '#1B3A5C' : '#fff',
                color: !chatRecipient ? '#fff' : '#64748B' }}>
              Everyone
              {dmUnread['everyone'] && chatRecipient !== null && (
                <span style={{ position: 'absolute', top: 2, right: 2, width: 7, height: 7, borderRadius: '50%', background: '#C1272D' }} />
              )}
            </button>
            {teamMembers.map(m => (
              <button key={m.id} onClick={() => switchConversation(m.id)}
                style={{ padding: '5px 12px', borderRadius: 20, border: '1px solid', fontSize: 11, fontWeight: 600, cursor: 'pointer', flexShrink: 0, position: 'relative',
                  borderColor: chatRecipient === m.id ? '#1B3A5C' : '#E2E8F0',
                  background: chatRecipient === m.id ? '#1B3A5C' : '#fff',
                  color: chatRecipient === m.id ? '#fff' : '#64748B' }}>
                {m.full_name.split(' ')[0]}
                {dmUnread[m.id] && chatRecipient !== m.id && (
                  <span style={{ position: 'absolute', top: 2, right: 2, width: 7, height: 7, borderRadius: '50%', background: '#C1272D' }} />
                )}
              </button>
            ))}
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {chatLoading && messages.length === 0 && (
              <div style={{ textAlign: 'center', color: '#94A3B8', fontSize: 13, marginTop: 24 }}>Loading…</div>
            )}
            {!chatLoading && messages.length === 0 && (
              <div style={{ textAlign: 'center', color: '#94A3B8', fontSize: 13, marginTop: 40 }}>
                {chatRecipient ? `Start a private conversation with ${recipientName}` : 'No messages yet. Start the conversation!'}
              </div>
            )}
            {messages.map((m, i) => {
              const isMe = m.sender_id === companyUser?.id;
              const showName = !isMe && (i === 0 || messages[i - 1]?.sender_id !== m.sender_id);
              const msgAttachments = Array.isArray(m.attachments) && m.attachments.length ? m.attachments
                : m.file_url ? [{ url: m.file_url, name: m.file_name, type: m.file_type }] : [];
              return (
                <div key={m.id} style={{ display: 'flex', flexDirection: isMe ? 'row-reverse' : 'row', alignItems: 'flex-end', gap: 8 }}>
                  {!isMe && (
                    <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#1B3A5C', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 11, fontWeight: 700, color: '#fff' }}>
                      {m.sender_name?.[0]?.toUpperCase() || '?'}
                    </div>
                  )}
                  <div style={{ maxWidth: '72%' }}>
                    {showName && <div style={{ fontSize: 10, color: '#94A3B8', marginBottom: 3, fontWeight: 600 }}>{m.sender_name}</div>}
                    <div style={{
                      padding: '9px 12px', borderRadius: isMe ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                      background: isMe ? '#1B3A5C' : '#F1F5F9',
                      color: isMe ? '#fff' : '#0F172A', fontSize: 13, lineHeight: 1.5,
                    }}>
                      {m.body && <div style={{ wordBreak: 'break-word' }}>{m.body}</div>}
                      {msgAttachments.map((att, idx) => (
                        <div key={idx} style={{ marginTop: m.body ? 6 : 0 }}>
                          {att.type?.startsWith('image/') ? (
                            <img src={fileUrl(att.url)} alt={att.name} style={{ maxWidth: '100%', borderRadius: 8 }} />
                          ) : (
                            <a href={fileUrl(att.url)} target="_blank" rel="noreferrer"
                              style={{ fontSize: 12, color: isMe ? '#93C5FD' : '#2563EB', display: 'flex', alignItems: 'center', gap: 4, wordBreak: 'break-all' }}>
                              📎 {att.name}
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                    <div style={{ fontSize: 10, color: '#CBD5E1', marginTop: 3, textAlign: isMe ? 'right' : 'left' }}>
                      {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={chatBottomRef} />
          </div>

          {/* Attached files preview */}
          {chatFiles.length > 0 && (
            <div style={{ padding: '8px 12px', background: '#F0FDF4', borderTop: '1px solid #BBF7D0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: '#16A34A', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {chatFiles.length} file{chatFiles.length !== 1 ? 's' : ''} selected
                </span>
                <button onClick={() => { setChatFiles([]); if (chatFileRef.current) chatFileRef.current.value = ''; }}
                  style={{ background: 'none', border: 'none', fontSize: 11, color: '#DC2626', cursor: 'pointer', fontWeight: 600, padding: 0 }}>
                  Clear all
                </button>
              </div>
              {chatFiles.map((f, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                  <span style={{ fontSize: 11, color: '#16A34A', fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📎 {f.name}</span>
                  <button onClick={() => setChatFiles(prev => prev.filter((_, j) => j !== i))}
                    style={{ background: 'none', border: 'none', color: '#DC2626', fontSize: 14, cursor: 'pointer', lineHeight: 1, flexShrink: 0 }}>×</button>
                </div>
              ))}
            </div>
          )}

          {/* Input bar */}
          <div style={{ padding: '12px 12px', borderTop: '1px solid #E2E8F0', background: '#FAFAFA' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <label style={{ cursor: 'pointer', flexShrink: 0, padding: '8px 10px', border: '1.5px solid #E2E8F0', borderRadius: 10, background: '#fff', display: 'flex', alignItems: 'center', color: '#64748B' }} title="Attach files (multiple)">
                <input ref={chatFileRef} type="file" multiple style={{ display: 'none' }}
                  onChange={e => setChatFiles(prev => [...prev, ...Array.from(e.target.files || [])])} />
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                </svg>
              </label>
              <textarea
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendChat(); } }}
                placeholder={chatRecipient ? `Private message to ${recipientName}…` : 'Message everyone… (Enter to send)'}
                rows={1}
                style={{ flex: 1, padding: '9px 12px', border: '1.5px solid #E2E8F0', borderRadius: 10, fontSize: 13, fontFamily: F, resize: 'none', outline: 'none', lineHeight: 1.5, minHeight: 38, maxHeight: 120, overflowY: 'auto', background: '#fff' }}
              />
              <button onClick={handleSendChat} disabled={chatSending || (!chatInput.trim() && !chatFiles.length)}
                style={{ flexShrink: 0, padding: '9px 14px', border: 'none', borderRadius: 10, background: (chatInput.trim() || chatFiles.length) ? '#1B3A5C' : '#E2E8F0', color: '#fff', cursor: (chatInput.trim() || chatFiles.length) ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 600, fontFamily: F }}>
                {chatSending ? '…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global Search Modal */}
      {showSearch && (
        <div onClick={() => setShowSearch(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 9000,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 80,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: '#fff', borderRadius: 16, width: '100%', maxWidth: 560, boxShadow: '0 24px 60px rgba(0,0,0,0.22)', overflow: 'hidden',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid #F1F5F9' }}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input ref={searchInputRef} value={searchQ} onChange={e => setSearchQ(e.target.value)}
                placeholder="Search candidates, jobs, assessments…"
                style={{ flex: 1, border: 'none', outline: 'none', fontSize: 15, color: '#0F172A', fontFamily: F, background: 'transparent' }} />
              {searchBusy && (
                <div style={{ width: 16, height: 16, border: '2px solid #E2E8F0', borderTopColor: '#1B3A5C', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
              )}
              <kbd onClick={() => setShowSearch(false)} style={{ fontSize: 11, color: '#94A3B8', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 5, padding: '2px 6px', cursor: 'pointer' }}>Esc</kbd>
            </div>
            <div style={{ maxHeight: 420, overflowY: 'auto' }}>
              {!searchQ.trim() && (
                <div style={{ padding: '28px 18px', textAlign: 'center', fontSize: 13, color: '#94A3B8' }}>
                  Search across all candidates, applications, jobs, and assessments
                </div>
              )}
              {searchQ.trim() && !searchBusy && searchRes && Object.keys(searchRes).length === 0 && (
                <div style={{ padding: '28px 18px', textAlign: 'center', fontSize: 13, color: '#94A3B8' }}>No results for "{searchQ}"</div>
              )}
              {searchRes && Object.entries(searchRes).map(([type, items]) => items?.length > 0 && (
                <div key={type}>
                  <div style={{ padding: '10px 18px 4px', fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {SEARCH_TYPE_LABEL[type] || type}
                  </div>
                  {items.map((item, idx) => (
                    <div key={idx} onClick={() => { navigate(SEARCH_TYPE_HREF[type]?.(item.id) || '/company'); setShowSearch(false); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 18px', cursor: 'pointer', borderTop: '1px solid #F8FAFC' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <div style={{ width: 32, height: 32, borderRadius: 8, background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <i className={type === 'candidate' ? 'ti-user' : type === 'application' ? 'ti-file-description' : type === 'assessment' ? 'ti-file-check' : 'ti-briefcase'} style={{ fontSize: 14, color: '#1B3A5C' }} />
                      </div>
                      <div style={{ overflow: 'hidden' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#0F172A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name || item.title || item.full_name || item.job_title}</div>
                        {item.subtitle && <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 2 }}>{item.subtitle}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function CompanyProtectedRoute({ children }) {
  const { companyUser, loading } = useCompanyAuth();

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 32, height: 32, border: '3px solid #1B3A5C', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    </div>
  );

  if (!companyUser) {
    window.location.href = '/company/login';
    return null;
  }

  if (!companyUser.company_is_activated) {
    return <Navigate to="/company/activate" replace />;
  }

  return children;
}
